import React, {useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface PlanInputDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (analysisContext: string, planInstructions: string) => void;
  trialId: string;
  version1: string;
  version2: string;
}

export const PlanInputDialog: React.FC<PlanInputDialogProps> = ({
  open,
  onClose,
  onGenerate,
  trialId,
  version1,
  version2
}) => {
  const [analysisContext, setAnalysisContext] = useState('');
  const [planInstructions, setPlanInstructions] = useState('');

  const handleGenerate = () => {
    onGenerate(analysisContext, planInstructions);
    // Reset fields for next time
    setAnalysisContext('');
    setPlanInstructions('');
  };

  const handleClose = () => {
    onClose();
    // Reset fields
    setAnalysisContext('');
    setPlanInstructions('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Generate Protocol Modification Plan
      </DialogTitle>

      <DialogContent>
        <Box sx={{mb: 3}}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Protocol: <strong>{trialId}</strong> | PRS: <strong>{version1}</strong> → <strong>{version2}</strong>
          </Typography>
        </Box>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{mb: 3}}>
          <Typography variant="body2">
            <strong>Optional:</strong> Provide additional context to guide the AI agents.
            These fields help the system understand protocol-specific requirements and constraints.
          </Typography>
        </Alert>

        <TextField
          label="Additional Context for Impact Analysis"
          multiline
          rows={4}
          fullWidth
          value={analysisContext}
          onChange={(e) => setAnalysisContext(e.target.value)}
          placeholder="Example: Visit 2 is critical for primary endpoint. Ensure diary logic is preserved and the 14-day window requirement is maintained."
          helperText="This context helps the Protocol Analyser understand which protocol features are affected by PRS changes."
          sx={{mb: 3}}
        />

        <TextField
          label="Instructions for Plan Generation"
          multiline
          rows={4}
          fullWidth
          value={planInstructions}
          onChange={(e) => setPlanInstructions(e.target.value)}
          placeholder="Example: Minimize changes to visit scheduling. Prefer modifying existing logic over removing elements. If removing elements, ensure all dependencies are clearly identified."
          helperText="These instructions guide the Orchestrator in generating the modification plan."
        />

        <Box sx={{mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1}}>
          <Typography variant="caption" color="text.secondary">
            ⏱️ <strong>Estimated time:</strong> 8-10 minutes
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          color="primary"
          size="large"
        >
          Generate Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};
